import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// Cache-API keys shared with public/sw.js: the SW stashes a tapped notification's route, the page
// reads it back on visibilitychange. NOT postMessage — that's lost when an iOS PWA resumes.
const PENDING_CACHE = 'parsley-pending-nav-v1'
const PENDING_KEY = '/__pending_navigation__'
// Guard against a stale stash hijacking an unrelated later app open.
const PENDING_TTL_MS = 60000

// Routes the app to a tapped notification's target. Pulls the stashed route on
// mount (covers cold launch) and whenever the tab becomes visible (covers an
// already-running app that iOS resumes from the background). Headless.
export default function PushDeepLinkListener() {
  const navigate = useNavigate()

  useEffect(() => {
    if (typeof caches === 'undefined') return
    let active = true
    let consuming = false

    const consumePending = async () => {
      // Guard against overlapping reads (visibilitychange + the SW nudge can fire
      // near-simultaneously); the winner deletes the entry, the rest no-op.
      if (consuming) return
      consuming = true
      try {
        const cache = await caches.open(PENDING_CACHE)
        const res = await cache.match(PENDING_KEY)
        if (!res) return
        await cache.delete(PENDING_KEY)
        const { path, ts } = (await res.json()) as { path: string; ts: number }
        if (active && typeof path === 'string' && Date.now() - ts < PENDING_TTL_MS) {
          navigate(path)
        }
      } catch {
        // CacheStorage unavailable / malformed entry — nothing to route.
      } finally {
        consuming = false
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void consumePending()
    }

    // The SW nudges us to re-read after it stashes a route for an already-open app
    // (the warm-resume case, where no fresh visibilitychange fires after the stash).
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PARSLEY_CHECK_PENDING_NAV') void consumePending()
    }

    document.addEventListener('visibilitychange', onVisibility)
    if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', onMessage)
    // Cold launch: the app may have opened at start_url; pull immediately.
    void consumePending()

    return () => {
      active = false
      document.removeEventListener('visibilitychange', onVisibility)
      if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', onMessage)
    }
  }, [navigate])

  return null
}
